import { Chip, CircularProgress } from '@mui/material'
import CustomInput from '../../components/TextInput'
import CustomButton from '../../components/Button'
import { useContext, useEffect, useState } from 'react'
import CustomDatePicker from '../../components/DatePicker'
import { useSnackbar } from 'notistack'
import CustomModal from '../../containers/Modal'
import { TEducationalInfo, TProfessionalInfo, TRegisterFullInfo } from '../../hooks/useFormRegister'
import { Country, RegisterFormInfoTypes } from '../../helpers/enums'
import CustomDropdown from '../../components/Dropdown'
import CustomRadioButton from '../../components/RadioButton'
import { AppConfig, FetchResponse } from '../../models/shared'
import UserService from '../../services/UserService'
import AvatarUploader, { AvatarPlaceholder } from '../register/avatarUploader/AvatarUploader'
import FormList from '../register/FormList'
import { UserDTO } from '../../models/users'
import { dateFormatter, removeDuplicatesFromArray, validateEmail } from '../../helpers/utils'
import { RegisterContext } from '../../context/RegisterContext'
import AuthService from '../../services/AuthService'
import { VerifyExistenceResponse } from '../../models/auth'
import { useNavigate } from 'react-router-dom'
import useSignOut from 'react-auth-kit/hooks/useSignOut'
import dayjs from 'dayjs'
import CustomPhoneInput from '../../components/PhoneInput'
import CustomIconButton from '../../components/IconButton'
import EditIcon from '@mui/icons-material/Edit'
import SearchAbilitiesModal from '../../components/Modal/SearchAbilitiesModal'

const DEFAULT_ERRORS = {
    firstName: '',
    lastName: '',
    birthdate: '',
    phone: '',
    image: '',
    email: '',
    linkedin: '',
}

interface Props {
    handleClose: () => void
    user: UserDTO
    config?: AppConfig
}
const UpdateUserInfo = ({ handleClose, user, config }: Props) => {
    const { enqueueSnackbar } = useSnackbar()
    const navigate = useNavigate()
    const signOut = useSignOut()
    const [loading, setLoading] = useState<boolean>(true)
    const [pending, setPending] = useState<boolean>(false)
    const [isAbilitiesModalOpen, setIsAbilitiesModalOpen] = useState<boolean>(false)
    const [errors, setErrors] = useState<typeof DEFAULT_ERRORS>(DEFAULT_ERRORS)
    const [isAvatarUploadOpen, setIsAvatarUploadOpen] = useState<boolean>(false)
    const { registerInfo, personalInfo, professionalInfo, educationalInfo, saveInfo } =
        useContext(RegisterContext)

    useEffect(() => {
        if (!loading || registerInfo.documentValue !== '') return
        saveInfo(RegisterFormInfoTypes.Register, {
            documentValue: user.documentValue,
            documentType: user.documentType,
            email: user.email,
            password: '',
            repeatPassword: '',
            urlUserLinkedIn: user.urlUserLinkedIn,
        })
        saveInfo(RegisterFormInfoTypes.Personal, {
            firstName: user.firstName,
            lastName: user.lastName,
            birthdate: dateFormatter(new Date(user.dateOfBirth)),
            phone: user.phone,
            nationality: user.nationality,
            livesAbroad: user.livesAbroad,
            image: user.picture,
            abilities: user.abilities,
        })
        const workHistories = user.workHistories
        workHistories.reverse()
        workHistories.forEach((x) =>
            saveInfo(RegisterFormInfoTypes.Professional, {
                companyName: x.employerName,
                job: x.jobTitle,
                startDate: dateFormatter(new Date(x.startDate)),
                endingDate: x.endDate ? dateFormatter(new Date(x.endDate)) : '',
                responsabilities: x.description,
                country: x.employerCountry,
                abilities: x.abilities,
            }),
        )
        const universityGraduation = user.educationalHistories.find(
            (x) => x.school === config?.university,
        )
        const otherEducationalHistories = user.educationalHistories.filter(
            (x) => x.school !== config?.university,
        )
        const educationalHistories = universityGraduation
            ? [universityGraduation, ...otherEducationalHistories]
            : otherEducationalHistories
        educationalHistories.forEach((x) =>
            saveInfo(RegisterFormInfoTypes.Educational, {
                degree: x.degree,
                school: x.school,
                carrer: x.areaOfStudy,
                startDate: dateFormatter(new Date(x.startDate)),
                endingDate: x.endDate ? dateFormatter(new Date(x.endDate)) : '',
                country: x.country,
                abilities: x.abilities,
            }),
        )
        setLoading(false)
    }, [user, saveInfo, loading, registerInfo, config])

    const validateInfo = () => {
        const newErrors = {
            ...errors,
            phone:
                personalInfo.phone.length < 6 || personalInfo.phone.length > 20
                    ? 'El teléfono debe tener entre 6 y 20 caracteres'
                    : '',
            email: validateEmail(registerInfo.email) ? '' : 'El email ingresado no es válido.',
            linkedin:
                !!registerInfo.urlUserLinkedIn &&
                !registerInfo.urlUserLinkedIn.includes('linkedin.com')
                    ? 'El link no pertenece a LinkedIn'
                    : '',
        }

        setErrors(newErrors)
        return Object.values(newErrors).every((x) => x === '')
    }

    const disableSubmit = () =>
        Object.entries(personalInfo).some((x) => x[0] !== 'image' && x[1] === '') ||
        professionalInfo.length === 0 ||
        educationalInfo.length === 0 ||
        registerInfo.email === '' ||
        [...personalInfo.abilities, ...getIndirectAbilities()].length === 0

    const handleOnChange = (name: string, value: string) => {
        const newInfo = { ...personalInfo, [name]: value }
        saveInfo(RegisterFormInfoTypes.Personal, newInfo)
    }

    const handleRegisterInfoChange = (name: string, value: string) => {
        const newInfo = { ...registerInfo, [name]: value }
        saveInfo(RegisterFormInfoTypes.Register, newInfo)
    }

    const handleAbilityChange = (ability: string) => {
        const newInfo = {
            ...personalInfo,
            abilities: personalInfo.abilities.includes(ability)
                ? personalInfo.abilities.filter((x) => x !== ability)
                : [...personalInfo.abilities, ability],
        }
        saveInfo(RegisterFormInfoTypes.Personal, newInfo)
    }

    const handleSubmit = async () => {
        if (!validateInfo()) return
        let response: FetchResponse
        const emailChanged = registerInfo.email !== user.email
        setPending(true)

        if (emailChanged) {
            response = await new AuthService().verifyExistence(registerInfo)
            if (response.status !== 200) {
                enqueueSnackbar('Error en la solicitud.', {
                    variant: 'error',
                    autoHideDuration: 6000,
                })
                return
            }

            const data: VerifyExistenceResponse = response.data
            const newErrors = {
                ...errors,
                email: !data.emailAvailable
                    ? 'Ya existe una cuenta asociada al email ingresado'
                    : '',
            }
            setErrors(newErrors)
            if (Object.values(newErrors).some((x) => x !== '')) {
                return
            }
        }

        localStorage.removeItem('linkedinToken')
        response = await new UserService().updateUser({
            registerInfo,
            personalInfo,
            professionalInfo,
            educationalInfo,
        } as TRegisterFullInfo)

        if (response.status === 200) {
            enqueueSnackbar('Información actualizada correctamente', {
                variant: 'success',
                autoHideDuration: 6000,
            })
            if (emailChanged) signOut()
            setTimeout(() => navigate(0), 1500)
        } else {
            enqueueSnackbar('Error en la solicitud.', {
                variant: 'error',
                autoHideDuration: 6000,
            })
            handleClose()
        }
    }

    const getIndirectAbilities = () => {
        const professional = professionalInfo.map((x) => x.abilities).flat()
        const educational = educationalInfo.map((x) => x.abilities).flat()
        return [...professional, ...educational]
    }

    return (
        <div className="w-full min-h-full flex justify-center items-center">
            <div className="flex flex-col w-[65vw] md:w-[50vw] max-h-[50vh] md:max-h-[75vh] gap-2 md:gap-0 px-2 md:px-3 md:pb-2 text-primary overflow-auto">
                <span className="text-lg md:text-2xl">Actualizar información</span>
                <span className="mb-1 md:my-3 text-base md:text-xl">Información personal</span>
                <div className="h-fit md:h-[140px]">
                    <div className="flex items-center gap-2 md:gap-4 h-full">
                        <div
                            className={`ml-2 h-[85px] md:h-full aspect-square rounded-full outline outline-primary -outline-offset-1 outline-[2px] cursor-pointer`}
                            onClick={() => {
                                setIsAvatarUploadOpen(true)
                            }}
                        >
                            {personalInfo.image !== '' ? (
                                <img
                                    width="100%"
                                    height="100%"
                                    alt="userImage"
                                    src={personalInfo.image}
                                    className="rounded-full"
                                />
                            ) : (
                                <AvatarPlaceholder />
                            )}
                        </div>
                        <div className="flex flex-col gap-2 md:gap-4 w-full">
                            <CustomInput
                                type="text"
                                label="Nombres"
                                name="firstName"
                                value={personalInfo.firstName}
                                onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                            />
                            <CustomInput
                                type="text"
                                label="Apellidos"
                                name="lastName"
                                value={personalInfo.lastName}
                                onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                            />
                        </div>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 md:mt-3">
                    <CustomInput
                        type="text"
                        label="Email"
                        name="email"
                        errorText={errors.email}
                        value={registerInfo.email}
                        onChange={(e) => handleRegisterInfoChange(e.target.name, e.target.value)}
                    />
                    <CustomPhoneInput
                        value={personalInfo.phone}
                        onChange={(e) => handleOnChange('phone', e)}
                    />
                    <CustomDatePicker
                        label="Fecha de nacimiento"
                        name="birthdate"
                        value={personalInfo.birthdate}
                        onChange={handleOnChange}
                        maxValue={dayjs(Date.now())}
                    />
                    <CustomInput
                        type="text"
                        label="Link a tu perfil de LinkedIn"
                        placeholder="Link a tu perfil de LinkedIn"
                        name="urlUserLinkedIn"
                        value={registerInfo.urlUserLinkedIn}
                        onChange={(e) => handleRegisterInfoChange(e.target.name, e.target.value)}
                        errorText={errors.linkedin}
                    />
                    <CustomDropdown
                        label="Nacionalidad"
                        name="nationality"
                        data={Object.keys(Country)}
                        value={personalInfo.nationality}
                        onChange={(e) => handleOnChange(e.target.name, e.target.value as string)}
                    />
                    <CustomRadioButton
                        name="livesAbroad"
                        value={personalInfo.livesAbroad}
                        onChange={(e) =>
                            saveInfo(RegisterFormInfoTypes.Personal, {
                                ...personalInfo,
                                livesAbroad: e.target.value === 'true',
                            })
                        }
                        label="¿Vivís en el extranjero?"
                        className="md:ml-1 text-[#777777] text-xs md:text-base"
                    />
                </div>
                <FormList<TProfessionalInfo>
                    infoArray={professionalInfo}
                    title="Información Profesional"
                    type={RegisterFormInfoTypes.Professional}
                />
                <FormList<TEducationalInfo>
                    infoArray={educationalInfo}
                    title="Educación"
                    type={RegisterFormInfoTypes.Educational}
                />
                <div className="flex gap-1 md:gap-2 mt-1 md:mt-3 items-center">
                    <span className="text-base md:text-xl">Aptitudes</span>
                    <CustomIconButton
                        className="h-fit"
                        icon={<EditIcon />}
                        onClick={() => setIsAbilitiesModalOpen(true)}
                    />
                </div>
                {personalInfo.abilities.length > 0 || getIndirectAbilities().length > 0 ? (
                    <div className="flex gap-1 items-center mt-2 flex-wrap">
                        {personalInfo.abilities.map((ability) => (
                            <div key={`selected${ability}`}>
                                <Chip
                                    label={ability}
                                    className="bg-primary text-white"
                                    sx={{
                                        '& .MuiChip-deleteIcon': {
                                            color: 'white',
                                        },
                                        '&:hover .MuiChip-deleteIcon': {
                                            color: 'white',
                                        },
                                    }}
                                />
                            </div>
                        ))}
                        {removeDuplicatesFromArray(
                            getIndirectAbilities().filter(
                                (x) => !personalInfo.abilities.includes(x as string),
                            ),
                        ).map((ability) => (
                            <div key={`selected${ability}`}>
                                <Chip
                                    label={ability}
                                    className="bg-disabled text-primary font-[500]"
                                    sx={{
                                        '& .MuiChip-deleteIcon': {
                                            color: 'white',
                                        },
                                        '&:hover .MuiChip-deleteIcon': {
                                            color: 'white',
                                        },
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                ) : (
                    <span className="text-xs md:text-base">
                        Seleccioná las aptitudes o habilidades que creas que te identifican mejor.
                    </span>
                )}
                <div className="grid grid-cols-2 gap-4">
                    <CustomButton
                        className="w-full md:mt-6 h-[36px]"
                        onClick={handleClose}
                        secondary
                        text="Cerrar"
                    />
                    <CustomButton
                        className="w-full md:mt-6 h-[36px]"
                        onClick={handleSubmit}
                        disabled={disableSubmit() || pending}
                    >
                        {!pending ? (
                            'Enviar'
                        ) : (
                            <CircularProgress
                                className="text-primary"
                                size={24}
                            />
                        )}
                    </CustomButton>
                </div>
            </div>
            <CustomModal
                isOpen={isAvatarUploadOpen}
                handleClose={() => setIsAvatarUploadOpen(false)}
            >
                <AvatarUploader
                    handleClose={() => setIsAvatarUploadOpen(false)}
                    setAvatarImage={(image?: string) => handleOnChange('image', image!)}
                />
            </CustomModal>
            <CustomModal
                isOpen={isAbilitiesModalOpen}
                handleClose={() => setIsAbilitiesModalOpen(false)}
            >
                <SearchAbilitiesModal
                    text="Seleccionar aptitudes"
                    handleClose={() => setIsAbilitiesModalOpen(false)}
                    selectedAbilities={personalInfo.abilities}
                    setSelectedAbility={handleAbilityChange}
                />
            </CustomModal>
        </div>
    )
}

export default UpdateUserInfo
